import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  ArrowLeft, 
  Send, 
  Paperclip, 
  X, 
  User, 
  AlertCircle,
  FileText
} from 'lucide-react';
import { useEmployees, useCreateMessage } from '@/hooks/use-messagerie';
import { cn } from '@/lib/utils';

const CreateMessageForm = ({ onBack, onSuccess }) => {
  const [formData, setFormData] = useState({
    destinataire_id: '',
    objet: '',
    message: '',
    description: '',
    motif: '',
    justification: '',
    type_demande: 'message',
    priorite: 'normale',
  });
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [errors, setErrors] = useState({});

  const { data: employeesData, isLoading: employeesLoading } = useEmployees();
  const createMessageMutation = useCreateMessage();

  const employees = employeesData?.data || [];

  // Gestion des changements de formulaire
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Supprimer l'erreur pour ce champ
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Gestion du fichier joint
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Vérification de la taille (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          file: 'Le fichier ne doit pas dépasser 10MB'
        }));
        return;
      }
      
      // Vérification du type de fichier
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 
                           'application/pdf', 'application/msword', 
                           'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                           'text/plain', 'application/vnd.ms-excel',
                           'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          file: 'Type de fichier non autorisé. Formats acceptés: JPG, PNG, PDF, DOC, DOCX, TXT, XLS, XLSX'
        }));
        return;
      }
      
      setSelectedFile(file);
      setErrors(prev => ({
        ...prev,
        file: null
      }));
    }
  };

  // Supprimer le fichier sélectionné
  const handleRemoveFile = () => {
    setSelectedFile(null);
    // Reset de l'input file
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.destinataire_id) {
      newErrors.destinataire_id = 'Le destinataire est requis';
    }
    
    if (!formData.objet.trim()) {
      newErrors.objet = 'L\'objet est requis';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    createMessageMutation.mutate(
      { messageData: formData, file: selectedFile },
      {
        onSuccess: () => {
          onSuccess && onSuccess();
        }
      }
    );
  };

  // Types de demande avec leurs labels
  const typeDemandeOptions = [
    { value: 'message', label: 'Message général' },
    { value: 'rh', label: 'Demande RH' },
    { value: 'technique', label: 'Support technique' },
    { value: 'formation', label: 'Formation' },
    { value: 'conge', label: 'Congés' },
    { value: 'materiel', label: 'Matériel' },
    { value: 'autre', label: 'Autre' },
  ];

  const prioriteOptions = [
    { value: 'normale', label: 'Normale' },
    { value: 'importante', label: 'Importante' },
    { value: 'urgente', label: 'Urgente' },
  ];

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Nouveau message</h1>
            <p className="text-muted-foreground">
              Envoyez un message à un collègue
            </p>
          </div>
        </div>

        {/* Formulaire */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Composer un message
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Destinataire */}
              <div className="space-y-2">
                <Label htmlFor="destinataire">Destinataire *</Label>
                <Select 
                  value={formData.destinataire_id} 
                  onValueChange={(value) => handleInputChange('destinataire_id', value)}
                  disabled={employeesLoading}
                >
                  <SelectTrigger className={cn(errors.destinataire_id && "border-destructive")}>
                    <SelectValue placeholder="Sélectionner un destinataire" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          <div>
                            <div className="font-medium">
                              {employee.prenom} {employee.nom}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {employee.departement?.nom} • {employee.role}
                            </div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.destinataire_id && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.destinataire_id}
                  </p>
                )}
              </div>

              {/* Type de demande et Priorité */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="type-demande">Type de demande</Label>
                  <Select 
                    value={formData.type_demande} 
                    onValueChange={(value) => handleInputChange('type_demande', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {typeDemandeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priorite">Priorité</Label>
                  <Select 
                    value={formData.priorite} 
                    onValueChange={(value) => handleInputChange('priorite', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {prioriteOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Objet */}
              <div className="space-y-2">
                <Label htmlFor="objet">Objet *</Label>
                <Input
                  id="objet"
                  value={formData.objet}
                  onChange={(e) => handleInputChange('objet', e.target.value)}
                  placeholder="Objet de votre message"
                  className={cn(errors.objet && "border-destructive")}
                />
                {errors.objet && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.objet}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description courte</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Résumé de votre demande"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder="Rédigez votre message ici..."
                  rows={6}
                  className={cn(errors.message && "border-destructive")}
                />
                {errors.message && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Champs optionnels selon le type */}
              {(formData.type_demande === 'rh' || formData.type_demande === 'formation') && (
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="motif">Motif</Label>
                    <Input
                      id="motif"
                      value={formData.motif}
                      onChange={(e) => handleInputChange('motif', e.target.value)}
                      placeholder="Motif de la demande"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="justification">Justification</Label>
                    <Input
                      id="justification"
                      value={formData.justification}
                      onChange={(e) => handleInputChange('justification', e.target.value)}
                      placeholder="Justification de la demande"
                    />
                  </div>
                </div>
              )}

              {/* Fichier joint */}
              <div className="space-y-2">
                {/* <Label htmlFor="file-input">Fichier joint (optionnel)</Label> */}
                <div className="flex items-center gap-4">
                  {/* <div className="flex-1">
                    <Input
                      id="file-input"
                      type="file"
                      onChange={handleFileChange}
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                      className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/80"
                    />
                  </div> */}
                  {/* {selectedFile && (
                    <div className="flex items-center gap-2 p-2 bg-muted rounded-md">
                      <FileText className="h-4 w-4" />
                      <span className="text-sm">{selectedFile.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveFile}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )} */}

                </div>
                {/* {errors.file && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.file}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Formats acceptés: JPG, PNG, PDF, DOC, DOCX, TXT, XLS, XLSX (max 10MB)
                </p> */}
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-4 pt-4">
                <Button 
                  type="submit" 
                  disabled={createMessageMutation.isLoading}
                  className="flex-1 md:flex-none"
                >
                  {createMessageMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Envoyer le message
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={onBack}>
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateMessageForm;
